import { useState } from 'react'

import { useIsFolderOwner } from 'components/folderPage/folderHooks'

import { store } from 'redux/store'
import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_FOLDER_STATE } from 'redux/folders/actions/types'

import folderService from 'services/folder.service'
import ToastService from 'services/toast.service'

import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import Icon from 'components/elements/Icon'
import ExportMultipleDecks from 'components/folderPage/ExportMultipleDecks'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'
import EditMultipleDecksSettings from 'components/folderPage/EditMultipleDecksSettings'
import MoveToArbitraryFolderOverlay from 'components/folderPage/MoveToArbitraryFolderOverlay'

import { generateDeckUrl } from 'utils/deckSeo'

import styles from './multiSelectControls.module.scss'

const MultiSelectControls = () => {
  const isFolderOwner = useIsFolderOwner()

  const [exportOpen, setExportOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [moveToArbitraryFolder, setMoveToArbitraryFolder] = useState(false)
  const [loading, setLoading] = useState(false)

  const [setFolderState] = useActionless(SET_FOLDER_STATE)

  const subfolders = useAppSelector(state => state.folders.rootFolder.subfolders)
  const parentFolder = useAppSelector(state => state.folders.rootFolder.parentFolder)
  const selectedFolders = useAppSelector(state => state.folders.selectedSubfolders)
  const selectedDecks = useAppSelector(state => state.folders.selectedDecks)

  const selectedDeckIds = Object.keys(selectedDecks).filter(id => selectedDecks[Number(id)])
  const selectedSubfolderIds = Object.keys(selectedFolders).filter(id => selectedFolders[Number(id)])

  const totalSubfolders = selectedSubfolderIds.length
  const totalDecks = selectedDeckIds.length

  const handleInvertFolderSelection = () => {
    const { subfolders } = store.getState().folders.rootFolder

    const newSelectedSubfolders = { ...selectedFolders }

    for (const subfolder of subfolders) {
      newSelectedSubfolders[subfolder.id] = !newSelectedSubfolders[subfolder.id]
    }

    setFolderState({ selectedSubfolders: newSelectedSubfolders })
  }

  const handleInvertDeckSelection = () => {
    const { decks } = store.getState().folders.rootFolder

    const newSelectedDecks = { ...selectedDecks }

    for (const deck of decks) {
      newSelectedDecks[deck.id] = !newSelectedDecks[deck.id]
    }

    setFolderState({ selectedDecks: newSelectedDecks })
  }

  const handleOpenRandomDeck = () => {
    const { decks } = store.getState().folders.rootFolder
    const deckIds = Object.keys(selectedDecks).filter(id => selectedDecks[Number(id)])

    const randomDeckIndex = Math.floor(Math.random() * deckIds.length)
    const randomDeckId = deckIds[randomDeckIndex]
    const randomDeck = decks.find(deck => deck.id === Number(randomDeckId))

    const url = generateDeckUrl(randomDeckId, randomDeck?.name)

    window?.open(url, '_blank')?.focus()
  }

  const handleDeleteSelectedItems = () => {
    const deletingSubFolders = Object.keys(selectedFolders)
      .filter(id => selectedFolders[Number(id)])
      .map(id => ({ id: Number(id), type: 'folder' }))

    const deletingDecks = Object.keys(selectedDecks)
      .filter(id => selectedDecks[Number(id)])
      .map(id => ({ id: Number(id), type: 'deck' }))

    return folderService
      .deleteItems([...deletingSubFolders, ...deletingDecks])
      .then(() => folderService.details())
      .then(_data => {
        const { rootFolder } = store.getState().folders
        const { subfolders, decks } = rootFolder

        const updatedRootFolder = {
          ...rootFolder,
          subfolders: subfolders.filter(subfolder => !selectedFolders[subfolder.id]),
          decks: decks.filter(deck => !selectedDecks[deck.id]),
        }

        setFolderState({ selectedDecks: {}, selectedSubfolders: {}, rootFolder: updatedRootFolder })
        setDeleteOpen(false)
      })
      .catch(ToastService.create)
  }

  const handleMoveSelectedToFolder = (folderId: number) => {
    // prettier-ignore
    if (selectedFolders[folderId]) return ToastService.create('Cannot move folder into itself', 'Folder Service', 'error')

    const movedSubfolders = Object.keys(selectedFolders)
      .filter(id => selectedFolders[Number(id)])
      .map(id => ({ id: Number(id), type: 'folder', patch: { parentFolder: folderId } }))

    const movedDecks = Object.keys(selectedDecks)
      .filter(id => selectedDecks[Number(id)])
      .map(id => ({ id: Number(id), type: 'deck', patch: { parentFolder: folderId } }))

    setLoading(true)

    folderService
      .updateItems([...movedSubfolders, ...movedDecks].map(item => ({ ...item, parentFolderId: folderId })))
      .then(() => {
        const { rootFolder } = store.getState().folders
        const { subfolders, decks } = rootFolder

        const updatedRootFolder = {
          ...rootFolder,
          subfolders: subfolders.filter(subfolder => !selectedFolders[subfolder.id]),
          decks: decks.filter(deck => !selectedDecks[deck.id]),
        }

        setFolderState({ selectedDecks: {}, selectedSubfolders: {}, rootFolder: updatedRootFolder })
      })
      .catch(ToastService.create)
      .finally(() => setLoading(false))
  }

  return (
    <>
      <div className={`${styles.bar} ${(totalSubfolders || totalDecks) && styles.shown}`}>
        <div className={styles.info}>
          <label>
            {!!totalSubfolders ? `${totalSubfolders} folder(s) ` : null}
            {!!totalSubfolders && !!totalDecks ? 'and' : null}
            {!!totalDecks ? ` ${totalDecks} deck(s) ` : null}
            selected
          </label>
        </div>

        <div className={styles.controls}>
          <button
            className={styles.button}
            onClick={() => setFolderState({ selectedDecks: {}, selectedSubfolders: {} })}>
            <Icon name="close" /> <span>Unselect</span>
          </button>

          <ArchidektDropdown
            triggerClassName={styles.dropdownTrigger}
            menuClassName={styles.quickOptions}
            defaultTriggerClassName={styles.defaultTrigger}
            direction="up"
            options={[
              {
                icon: 'arrow right',
                label: 'Invert folder selection',
                onClick: handleInvertFolderSelection,
                noCloseOnClick: true,
              },
              {
                icon: 'arrow right',
                label: 'Invert deck selection',
                onClick: handleInvertDeckSelection,
                noCloseOnClick: true,
              },
              { type: 'spacer' },
              {
                label: 'Delete selected items',
                onClick: () => setDeleteOpen(true),
                icon: 'trash alternate outline',
                hidden: !isFolderOwner,
              },
              {
                label: 'Move selected items to',
                icon: 'arrow right',
                type: 'extras',
                id: 'move-deck-to-other-dir',
                hidden: !isFolderOwner,
                options: [
                  {
                    label: 'Parent folder',
                    onClick: () => handleMoveSelectedToFolder(parentFolder.id),
                    hidden: !parentFolder?.id,
                  },
                  { type: 'spacer', hidden: !parentFolder?.id || !subfolders.length },
                  ...subfolders.map(subfolder => ({
                    label: subfolder.name,
                    onClick: () => handleMoveSelectedToFolder(subfolder.id),
                  })),
                  { type: 'spacer', hidden: !parentFolder?.id },
                  {
                    label: 'View full folder tree',
                    onClick: () => setMoveToArbitraryFolder(true),
                    icon: 'folder open',
                  },
                ],
              },
              { type: 'spacer', hidden: !isFolderOwner },
              {
                label: 'Edit selected decks',
                onClick: () => setFolderState({ editMultipleDecksSettingsOpen: true }),
                icon: 'edit',
                disabled: !totalDecks,
                hidden: !isFolderOwner,
              },
              {
                label: 'Export selected decks',
                onClick: () => setExportOpen(true),
                icon: 'download',
                disabled: !totalDecks,
              },
              { label: 'Open random deck', onClick: handleOpenRandomDeck, icon: 'random', disabled: !totalDecks },
            ]}
          />
        </div>
      </div>

      <EditMultipleDecksSettings />
      <ExportMultipleDecks open={exportOpen} onClose={() => setExportOpen(false)} />
      <ConfirmDeleteModal
        open={deleteOpen}
        delayEnabledTime={3000}
        header="Delete all seleted items"
        message={
          <>
            <div>Are you sure you want to delete all selected folders decks?</div>
            <br />
            <div className={styles.warning}>
              All decks <b>inside any selected sub-folders</b> will also be deleted.
            </div>
            <br />
            <div className={styles.warning}>This action cannot be undone.</div>
          </>
        }
        buttonText="Delete all"
        onClose={() => setDeleteOpen(false)}
        onDelete={handleDeleteSelectedItems}
      />
      <MoveToArbitraryFolderOverlay
        open={moveToArbitraryFolder}
        onClose={() => setMoveToArbitraryFolder(false)}
        deckIds={selectedDeckIds}
      />
    </>
  )
}

export default MultiSelectControls
